import { SearchMetaTypes } from './types';
import {
  definedEventsAccess,
  definedEventsReducer as definedEvents,
  getAllDefinedEvents,
} from './definedEvents';
import { SearchMetaActions } from './actions';
import {
  CustomField,
  customFieldCompareFn,
  FieldScope,
  parseUnscopedFieldName,
  VarType,
  varUpdateToHideKey,
} from '@fullstory/search-vars';
import { combineReducers } from '@fullstory/redux-util/utils';
import { isEqual } from 'lodash';
import { Scope, Type } from '@fullstory/proto/fs/api/vars';
import { CustomSchemaCounts, VarRule } from '@fullstory/proto/fs/services/fieldmeta';
import { unscopedVarName } from '@fullstory/util/vars';

const VarState = VarRule.VarState;

interface Loadable<K, V> {
  /**
   * Keys that are actively loading values.
   */
  loading: Set<K>;
  /**
   * The last time a value was loaded for a key.
   */
  lastLoaded: Map<K, Date>;
  /**
   * Key value pairs that have successfully loaded.
   */
  values: Map<K, V>;
}

export interface MetaState {
  /**
   * The names of all the custom events that currently exist for the current org.
   */
  customEventNames: string[];
  /**
   * Map of custom event fields by event name.
   */
  customEventMeta: Loadable<string, CustomField[]>;
  /**
   * The custom user properties for the org.
   */
  userFields: CustomField[];
  /**
   * Map of page fields by page id.
   */
  pageFields: Loadable<number, CustomField[]>;
  /**
   * Map of element fields by named element id.
   */
  elementFields: Loadable<string, CustomField[]>;

  /**
   * The counts contributing to the limits for custom schema fields.
   */
  counts: CustomSchemaCounts;
  /**
   * The limits for custom schema fields.
   */
  limits: CustomSchemaCounts;

  /**
   * Map of custom fields to their associated event connections.
   */
  eventConnectionsByField: Loadable<string, Set<string>>;

  /**
   * Map of custom fields to their associated page connections.
   */
  pageConnectionsByField: Loadable<string, Set<number>>;

  /**
   * Map of custom fields to their associated element connections.
   */
  elementConnectionsByField: Loadable<string, Set<string>>;

  /**
   * Hidden vars control what is shown in the UI. If the var is in this map, it will not populate
   * in any dropdowns. This applies to user and event scopes.
   * Hidden user vars are of the form: `user_email_int`
   * Hidden vars of the form: `:Created Segment` mean that the entire custom
   *   var and it's properties should not show up in the UI.
   * Hidden vars of the form: `evt_Version_real:Created Segment` mean that only that field
   *   will not show up in the dependent search UI for the `Created Segment` custom event.
   * Hidden page vars behave in the same way as user vars.
   */
  hiddenVars: Set<string>;

  /**
   * Whether the initial custom schema has been loaded.
   */
  loaded: boolean;

  /**
   * Fields that are internal Fsevt Vars
   * Currenlty hardcoded as this feature is still experimental
   */
  fsEvtFields: CustomField[];
}

const initialMetaState: MetaState = {
  customEventNames: [],
  customEventMeta: {
    loading: new Set<string>(),
    lastLoaded: new Map<string, Date>(),
    values: new Map<string, CustomField[]>(),
  },
  userFields: [],
  pageFields: {
    loading: new Set<number>(),
    lastLoaded: new Map<number, Date>(),
    values: new Map<number, CustomField[]>(),
  },
  elementFields: {
    loading: new Set<string>(),
    lastLoaded: new Map<string, Date>(),
    values: new Map<string, CustomField[]>(),
  },
  counts: {
    userProperties: 0,
    pageProperties: 0,
    events: 0,
    eventProperties: 0,
    elementProperties: 0,
  },
  limits: {
    userProperties: 0,
    pageProperties: 0,
    events: 0,
    eventProperties: 0,
    elementProperties: 0,
  },
  eventConnectionsByField: {
    loading: new Set<string>(),
    lastLoaded: new Map<string, Date>(),
    values: new Map<string, Set<string>>(),
  },
  pageConnectionsByField: {
    loading: new Set<string>(),
    lastLoaded: new Map<string, Date>(),
    values: new Map<string, Set<number>>(),
  },
  elementConnectionsByField: {
    loading: new Set<string>(),
    lastLoaded: new Map<string, Date>(),
    values: new Map<string, Set<string>>(),
  },
  hiddenVars: new Set<string>(),
  loaded: false,
  // TODO(aditya): these are hardcoded as this feature is still experimental
  // will need to be updated when this feature is fully supported
  fsEvtFields: [
    {
      scope: FieldScope.Fsevt,
      ident: 'flow_completion-flow_completion_v0_score',
      varType: VarType.Real,
    },
  ],
};

function meta(state: MetaState = initialMetaState, action: SearchMetaActions): MetaState {
  switch (action.type) {
    case SearchMetaTypes.GetCustomSchemaSuccess: {
      const userProperties = action.data.customSchema?.userProperties ?? [];
      const pageProperties = action.data.customSchema?.pageProperties ?? [];
      const events = action.data.customSchema?.events ?? [];
      const eventProperties = action.data.customSchema?.eventProperties ?? [];
      const elementProperties = action.data.customSchema?.elementProperties ?? [];
      const counts = action.data.customSchemaCounts;
      const limits = action.data.customSchemaLimits;

      const userFields = userProperties
        .map(u => parseUnscopedFieldName(unscopedVarName(u.name, u.type), FieldScope.User))
        .sort(customFieldCompareFn);

      const newPageFieldsLoading = state.pageFields?.loading ?? new Set<number>(); // never changes as part of initial custom schema load
      const newPageFieldsLastLoaded = state.pageFields?.lastLoaded ?? new Map<number, Date>();
      const newPageFields = new Map<number, CustomField[]>(state.pageFields?.values ?? new Map());
      newPageFieldsLastLoaded.set(0, new Date());
      newPageFields.set(
        0,
        pageProperties
          .map(p => parseUnscopedFieldName(unscopedVarName(p.name, p.type), FieldScope.Page))
          .sort(customFieldCompareFn),
      );

      const newElementFieldsLoading = state.elementFields?.loading ?? new Set<string>(); // never changes as part of initial custom schema load
      const newElementFieldsLastLoaded = state.elementFields?.lastLoaded ?? new Map<string, Date>();
      const newElementFields = new Map<string, CustomField[]>(
        state.elementFields?.values ?? new Map(),
      );
      newElementFieldsLastLoaded.set('', new Date());
      newElementFields.set(
        '',
        elementProperties
          .map(p => parseUnscopedFieldName(unscopedVarName(p.name, p.type), FieldScope.Element))
          .sort(customFieldCompareFn),
      );

      const customEventNames = events.map(e => e.name).sort();

      const newCustomEventMetaLoading = state.customEventMeta?.loading ?? new Set<string>(); // never changes as part of initial custom schema load
      const newCustomEventMetaLastLoaded =
        state.customEventMeta?.lastLoaded ?? new Map<string, Date>();
      const newCustomEventMetaFields = new Map<string, CustomField[]>(
        state.customEventMeta?.values ?? new Map(),
      ); // preserve map, other reducers update it

      const emptyEventName = ''; // initial load places all unscoped event properties under the empty string
      eventProperties.forEach(ep => {
        const fieldName = unscopedVarName(ep.name, ep.type);
        const customField = parseUnscopedFieldName(fieldName, FieldScope.Event);
        const customFields = newCustomEventMetaFields.get(emptyEventName);
        if (customFields) {
          customFields.push(customField);
        } else {
          newCustomEventMetaFields.set(emptyEventName, [customField]);
        }
      });
      newCustomEventMetaLastLoaded.set(emptyEventName, new Date());
      newCustomEventMetaFields.forEach(fields => fields.sort(customFieldCompareFn));

      return {
        ...state,
        userFields,
        customEventNames,
        customEventMeta: {
          loading:
            state.customEventMeta &&
            isEqual(state.customEventMeta.loading, newCustomEventMetaLoading)
              ? state.customEventMeta.loading
              : newCustomEventMetaLoading,
          lastLoaded:
            state.customEventMeta &&
            isEqual(state.customEventMeta.lastLoaded, newCustomEventMetaLastLoaded)
              ? state.customEventMeta.lastLoaded
              : newCustomEventMetaLastLoaded,
          values:
            state.customEventMeta && isEqual(state.customEventMeta.values, newCustomEventMetaFields)
              ? state.customEventMeta.values
              : newCustomEventMetaFields,
        },
        pageFields: {
          loading:
            state.pageFields && isEqual(state.pageFields.loading, newPageFieldsLoading)
              ? state.pageFields.loading
              : newPageFieldsLoading,
          lastLoaded:
            state.pageFields && isEqual(state.pageFields.lastLoaded, newPageFieldsLastLoaded)
              ? state.pageFields.lastLoaded
              : newPageFieldsLastLoaded,
          values:
            state.pageFields && isEqual(state.pageFields.values, newPageFields)
              ? state.pageFields.values
              : newPageFields,
        },
        elementFields: {
          loading:
            state.elementFields && isEqual(state.elementFields.loading, newElementFieldsLoading)
              ? state.elementFields.loading
              : newElementFieldsLoading,
          lastLoaded:
            state.elementFields &&
            isEqual(state.elementFields.lastLoaded, newElementFieldsLastLoaded)
              ? state.elementFields.lastLoaded
              : newElementFieldsLastLoaded,
          values:
            state.elementFields && isEqual(state.elementFields.values, newElementFields)
              ? state.elementFields.values
              : newElementFields,
        },
        counts: isEqual(state.counts, counts) ? state.counts : counts ?? state.counts,
        limits: isEqual(state.limits, limits) ? state.limits : limits ?? state.limits,
        loaded: true,
      };
    }
    case SearchMetaTypes.GetCustomSchemaConnectionsFailure: {
      const newCustomEventMetaLoading = new Set<string>(
        state.customEventMeta?.loading ?? new Set(),
      );
      const newPageFieldsLoading = new Set<number>(state.pageFields?.loading ?? new Set());
      const newElementFieldsLoading = new Set<string>(state.elementFields?.loading ?? new Set());
      action?.request?.body?.events?.forEach(e => newCustomEventMetaLoading.delete(e));
      action?.request?.body?.pages?.forEach(p => newPageFieldsLoading.delete(p));
      action?.request?.body?.elements?.forEach(e => newElementFieldsLoading.delete(e));
      return {
        ...state,
        customEventMeta: {
          ...state.customEventMeta,
          loading:
            state.customEventMeta &&
            isEqual(state.customEventMeta.loading, newCustomEventMetaLoading)
              ? state.customEventMeta.loading
              : newCustomEventMetaLoading,
        },
        pageFields: {
          ...state.pageFields,
          loading:
            state.pageFields && isEqual(state.pageFields.loading, newPageFieldsLoading)
              ? state.pageFields.loading
              : newPageFieldsLoading,
        },
        elementFields: {
          ...state.elementFields,
          loading:
            state.elementFields && isEqual(state.elementFields.loading, newElementFieldsLoading)
              ? state.elementFields.loading
              : newElementFieldsLoading,
        },
      };
    }
    case SearchMetaTypes.GetCustomSchemaConnectionsSuccess: {
      const newCustomEventMetaLoading = new Set<string>(
        state.customEventMeta?.loading ?? new Set(),
      );
      const newCustomEventMetaLastLoaded = new Map<string, Date>(
        state.customEventMeta?.lastLoaded ?? new Map(),
      );
      const newCustomEventMetaFields = new Map<string, CustomField[]>(
        state.customEventMeta?.values ?? new Map(),
      );
      const eventConnections = action.data.customSchemaConnections?.eventConnections ?? {};
      for (const eventName in eventConnections) {
        const conns = eventConnections[eventName] ?? { properties: [] };
        const eventProps = conns.properties
          .map(
            prop =>
              <CustomField>{
                scope: FieldScope.Event,
                ident: prop.name,
                varType: <VarType>Type[prop.type],
              },
          )
          .sort(customFieldCompareFn);
        newCustomEventMetaLoading.delete(eventName);
        newCustomEventMetaLastLoaded.set(eventName, new Date());
        newCustomEventMetaFields.set(eventName, eventProps);
      }
      const newPageFieldsLoading = new Set<number>(state.pageFields?.loading ?? new Set());
      const newPageFieldsLastLoaded = new Map<number, Date>(
        state.pageFields?.lastLoaded ?? new Map(),
      );
      const newPageFields = new Map<number, CustomField[]>(state.pageFields?.values ?? new Map());
      const pageConnections = action.data.customSchemaConnections?.pageConnections ?? {};
      // map keys are strings on the wire, need to parse numeric value
      for (const pageDefIdStr in pageConnections) {
        const pageDefId = parseInt(pageDefIdStr, 10);
        const conns = pageConnections[pageDefId] ?? { properties: [] };
        const pageProps = conns.properties
          .map(
            prop =>
              <CustomField>{
                scope: FieldScope.Page,
                ident: prop.name,
                varType: <VarType>Type[prop.type],
              },
          )
          .sort(customFieldCompareFn);
        newPageFieldsLoading.delete(pageDefId);
        newPageFieldsLastLoaded.set(pageDefId, new Date());
        newPageFields.set(pageDefId, pageProps);
      }
      const newElementFieldsLoading = new Set<string>(state.elementFields?.loading ?? new Set());
      const newElementFieldsLastLoaded = new Map<string, Date>(
        state.elementFields?.lastLoaded ?? new Map(),
      );
      const newElementFields = new Map<string, CustomField[]>(
        state.elementFields?.values ?? new Map(),
      );
      const elementConnections = action.data.customSchemaConnections?.elementConnections ?? {};
      for (const namedElementId in elementConnections) {
        const conns = elementConnections[namedElementId] ?? { properties: [] };
        const elementProps = conns.properties
          .map(
            prop =>
              <CustomField>{
                scope: FieldScope.Element,
                ident: prop.name,
                varType: <VarType>Type[prop.type],
              },
          )
          .sort(customFieldCompareFn);
        newElementFieldsLoading.delete(namedElementId);
        newElementFieldsLastLoaded.set(namedElementId, new Date());
        newElementFields.set(namedElementId, elementProps);
      }
      return {
        ...state,
        customEventMeta: {
          loading:
            state.customEventMeta &&
            isEqual(state.customEventMeta.loading, newCustomEventMetaLoading)
              ? state.customEventMeta.loading
              : newCustomEventMetaLoading,
          lastLoaded:
            state.customEventMeta &&
            isEqual(state.customEventMeta.lastLoaded, newCustomEventMetaLastLoaded)
              ? state.customEventMeta.lastLoaded
              : newCustomEventMetaLastLoaded,
          values:
            state.customEventMeta && isEqual(state.customEventMeta.values, newCustomEventMetaFields)
              ? state.customEventMeta.values
              : newCustomEventMetaFields,
        },
        pageFields: {
          loading:
            state.pageFields && isEqual(state.pageFields.loading, newPageFieldsLoading)
              ? state.pageFields.loading
              : newPageFieldsLoading,
          lastLoaded:
            state.pageFields && isEqual(state.pageFields.lastLoaded, newPageFieldsLastLoaded)
              ? state.pageFields.lastLoaded
              : newPageFieldsLastLoaded,
          values:
            state.pageFields && isEqual(state.pageFields.values, newPageFields)
              ? state.pageFields.values
              : newPageFields,
        },
        elementFields: {
          loading:
            state.elementFields && isEqual(state.elementFields.loading, newElementFieldsLoading)
              ? state.elementFields.loading
              : newElementFieldsLoading,
          lastLoaded:
            state.elementFields &&
            isEqual(state.elementFields.lastLoaded, newElementFieldsLastLoaded)
              ? state.elementFields.lastLoaded
              : newElementFieldsLastLoaded,
          values:
            state.elementFields && isEqual(state.elementFields.values, newElementFields)
              ? state.elementFields.values
              : newElementFields,
        },
      };
    }
    case SearchMetaTypes.MarkConnectionsLoading: {
      const customEventMetaLoading = new Set<string>(state.customEventMeta?.loading ?? new Set());
      const pageFieldsLoading = new Set<number>(state.pageFields?.loading ?? new Set());
      const elementFieldsLoading = new Set<string>(state.elementFields?.loading ?? new Set());
      action?.events?.forEach(e => customEventMetaLoading.add(e));
      action?.pages?.forEach(p => pageFieldsLoading.add(p));
      action?.elements?.forEach(p => elementFieldsLoading.add(p));
      return {
        ...state,
        customEventMeta: {
          ...state.customEventMeta,
          loading:
            state.customEventMeta && isEqual(state.customEventMeta.loading, customEventMetaLoading)
              ? state.customEventMeta.loading
              : customEventMetaLoading,
        },
        pageFields: {
          ...state.pageFields,
          loading:
            state.pageFields && isEqual(state.pageFields.loading, pageFieldsLoading)
              ? state.pageFields.loading
              : pageFieldsLoading,
        },
        elementFields: {
          ...state.elementFields,
          loading:
            state.elementFields && isEqual(state.elementFields.loading, elementFieldsLoading)
              ? state.elementFields.loading
              : elementFieldsLoading,
        },
      };
    }
    case SearchMetaTypes.MarkConnectionsByPropertyLoading: {
      const eventFieldsLoading = new Set<string>(state.eventConnectionsByField.loading);
      const pageFieldsLoading = new Set<string>(state.pageConnectionsByField.loading);
      const elementFieldsLoading = new Set<string>(state.elementConnectionsByField.loading);
      const properties = action?.properties;
      const scope = action?.scope;
      if (properties) {
        for (const prop of properties) {
          const fieldName = unscopedVarName(prop.name, prop.type);
          switch (scope) {
            case Scope.evt:
              eventFieldsLoading.add(fieldName);
              break;
            case Scope.page:
              pageFieldsLoading.add(fieldName);
              break;
            case Scope.elem:
              elementFieldsLoading.add(fieldName);
              break;
          }
        }
      }
      return {
        ...state,
        eventConnectionsByField: {
          ...state.eventConnectionsByField,
          loading:
            state.eventConnectionsByField &&
            isEqual(state.eventConnectionsByField.loading, eventFieldsLoading)
              ? state.eventConnectionsByField.loading
              : eventFieldsLoading,
        },
        pageConnectionsByField: {
          ...state.pageConnectionsByField,
          loading:
            state.pageConnectionsByField &&
            isEqual(state.pageConnectionsByField.loading, pageFieldsLoading)
              ? state.pageConnectionsByField.loading
              : pageFieldsLoading,
        },
        elementConnectionsByField: {
          ...state.elementConnectionsByField,
          loading:
            state.elementConnectionsByField &&
            isEqual(state.elementConnectionsByField.loading, elementFieldsLoading)
              ? state.elementConnectionsByField.loading
              : elementFieldsLoading,
        },
      };
    }
    case SearchMetaTypes.GetCustomSchemaConnectionsByPropertyFailure: {
      const eventFieldsLoading = new Set<string>(state.eventConnectionsByField.loading);
      const pageFieldsLoading = new Set<string>(state.pageConnectionsByField.loading);
      const elementFieldsLoading = new Set<string>(state.elementConnectionsByField.loading);
      const reqProperties = action?.request?.body?.properties;
      const scope = action?.request?.body?.scope;
      if (reqProperties) {
        for (const prop of reqProperties) {
          const fieldName = unscopedVarName(prop.name, prop.type);
          switch (scope) {
            case Scope.evt:
              eventFieldsLoading.delete(fieldName);
              break;
            case Scope.page:
              pageFieldsLoading.delete(fieldName);
              break;
            case Scope.elem:
              elementFieldsLoading.delete(fieldName);
              break;
          }
        }
      }
      return {
        ...state,
        eventConnectionsByField: {
          ...state.eventConnectionsByField,
          loading:
            state.eventConnectionsByField &&
            isEqual(state.eventConnectionsByField.loading, eventFieldsLoading)
              ? state.eventConnectionsByField.loading
              : eventFieldsLoading,
        },
        pageConnectionsByField: {
          ...state.pageConnectionsByField,
          loading:
            state.pageConnectionsByField &&
            isEqual(state.pageConnectionsByField.loading, pageFieldsLoading)
              ? state.pageConnectionsByField.loading
              : pageFieldsLoading,
        },
        elementConnectionsByField: {
          ...state.elementConnectionsByField,
          loading:
            state.elementConnectionsByField &&
            isEqual(state.elementConnectionsByField.loading, elementFieldsLoading)
              ? state.elementConnectionsByField.loading
              : elementFieldsLoading,
        },
      };
    }
    case SearchMetaTypes.GetCustomSchemaConnectionsByPropertySuccess: {
      const eventConnectionsByField = {
        loading: new Set<string>(state.eventConnectionsByField.loading),
        lastLoaded: new Map<string, Date>(state.eventConnectionsByField.lastLoaded),
        values: new Map<string, Set<string>>(state.eventConnectionsByField.values),
      };
      const pageConnectionsByField = {
        loading: new Set<string>(state.pageConnectionsByField.loading),
        lastLoaded: new Map<string, Date>(state.pageConnectionsByField.lastLoaded),
        values: new Map<string, Set<number>>(state.pageConnectionsByField.values),
      };
      const elementConnectionsByField = {
        loading: new Set<string>(state.elementConnectionsByField.loading),
        lastLoaded: new Map<string, Date>(state.elementConnectionsByField.lastLoaded),
        values: new Map<string, Set<string>>(state.elementConnectionsByField.values),
      };
      const reqProperties = action?.request?.body?.properties;
      const scope = action?.request?.body?.scope;
      if (reqProperties) {
        for (const prop of reqProperties) {
          const fieldName = unscopedVarName(prop.name, prop.type);
          // remove affected properties from loading and ensure each requested property is present (even if with empty connections)
          // there may not be data in the response referencing the property if there are no connections
          switch (scope) {
            case Scope.evt:
              eventConnectionsByField.loading.delete(fieldName);
              eventConnectionsByField.lastLoaded.set(fieldName, new Date());
              eventConnectionsByField.values.set(fieldName, new Set<string>());
              break;
            case Scope.page:
              pageConnectionsByField.loading.delete(fieldName);
              pageConnectionsByField.lastLoaded.set(fieldName, new Date());
              pageConnectionsByField.values.set(fieldName, new Set<number>());
              break;
            case Scope.elem:
              elementConnectionsByField.loading.delete(fieldName);
              elementConnectionsByField.lastLoaded.set(fieldName, new Date());
              elementConnectionsByField.values.set(fieldName, new Set<string>());
              break;
          }
        }
        const eventConnections = action.data?.customSchemaConnections?.eventConnections;
        if (eventConnections) {
          for (const eventName in eventConnections) {
            const properties = eventConnections[eventName]?.properties ?? [];
            for (const prop of properties) {
              const conns = eventConnectionsByField.values.get(
                unscopedVarName(prop.name, prop.type),
              );
              // should already exist from req properties, if not we won't set as it may not be safe
              conns?.add(eventName);
            }
          }
        }
        const pageConnections = action.data?.customSchemaConnections?.pageConnections;
        if (pageConnections) {
          for (const pageDefIdKey in pageConnections) {
            const pageDefId = parseInt(pageDefIdKey, 10);
            const properties = pageConnections[pageDefIdKey]?.properties ?? [];
            for (const prop of properties) {
              const conns = pageConnectionsByField.values.get(
                unscopedVarName(prop.name, prop.type),
              );
              // should already exist from req properties, if not we won't set as it may not be safe
              conns?.add(pageDefId);
            }
          }
        }
        const elementConnections = action.data?.customSchemaConnections?.elementConnections;
        if (elementConnections) {
          for (const namedElementId in elementConnections) {
            const properties = elementConnections[namedElementId]?.properties ?? [];
            for (const prop of properties) {
              const conns = elementConnectionsByField.values.get(
                unscopedVarName(prop.name, prop.type),
              );
              // should already exist from req properties, if not we won't set as it may not be safe
              conns?.add(namedElementId);
            }
          }
        }
      }
      return {
        ...state,
        eventConnectionsByField: isEqual(state.eventConnectionsByField, eventConnectionsByField)
          ? state.eventConnectionsByField
          : eventConnectionsByField,
        pageConnectionsByField: isEqual(state.pageConnectionsByField, pageConnectionsByField)
          ? state.pageConnectionsByField
          : pageConnectionsByField,
        elementConnectionsByField: isEqual(
          state.elementConnectionsByField,
          elementConnectionsByField,
        )
          ? state.elementConnectionsByField
          : elementConnectionsByField,
      };
    }
    case SearchMetaTypes.GetHiddenVarsSuccess: {
      const { hiddenVars, ...rest } = state;
      const newVars = new Set(action.data.vars);
      // Don't update state if they are the same
      if (isEqual(hiddenVars, newVars)) return state;
      return {
        hiddenVars: newVars,
        ...rest,
      };
    }
    case SearchMetaTypes.UpdateVarsSuccess: {
      const { hiddenVars, ...rest } = state;
      const newSet = new Set(hiddenVars);
      action.request.body!.updates.forEach(u =>
        u.state >= VarState.VAR_STATE_ARCHIVED
          ? newSet.add(varUpdateToHideKey(u))
          : newSet.delete(varUpdateToHideKey(u)),
      );
      return {
        hiddenVars: newSet,
        ...rest,
      };
    }
    default:
      return state;
  }
}

type SearchMetaReducerState = {
  definedEvents: ReturnType<typeof definedEvents>;
  definedEventsAccess: ReturnType<typeof definedEventsAccess>;
  getAllDefinedEvents: ReturnType<typeof getAllDefinedEvents>;
  meta: ReturnType<typeof meta>;
};

export const searchMetaReducer = combineReducers<SearchMetaReducerState>({
  definedEvents,
  definedEventsAccess,
  getAllDefinedEvents,
  meta,
});
